import React, { createContext, useContext, useMemo } from 'react';
import { Request } from './Request';

const OrdersContext = createContext(null);

const ORDERS_DB_KEY = 'user';

const readGlobalOrders = () => {
  try {
    const raw = localStorage.getItem(ORDERS_DB_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw).orders;
    return Array.isArray(parsed) ? parsed : parsed && Array.isArray(parsed.orders) ? parsed.orders : [];
  } catch {
    return [];
  }
};

const readCurrentUserObject = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCurrentUserObject = (userObj) => {
  try {
    if (userObj && typeof userObj === 'object') {
      localStorage.setItem('user', JSON.stringify(userObj));
    }
  } catch {
    // ignore
  }
};

const readUserOrders = () => {
  const u = readCurrentUserObject();
  const orders = u?.orders;
  return Array.isArray(orders) ? orders : [];
};

const writeUserOrders = (orders) => {
  const u = readCurrentUserObject();
  if (!u) return;
  const next = { ...u, orders: Array.isArray(orders) ? orders : [] };
  writeCurrentUserObject(next);
};

const generateOrderNumber = () => {
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `ORD-${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)}-${rand}`;
};

// Orders context provider
export const OrdersProvider = ({ children }) => {
  const placeOrder = async ({ user, items, total }) => {
    if (!user) throw new Error('Not authenticated');

    const body = {
      userId: user.id,
      items: items.map((it) => ({
        product_id: it.productId || it.product_id || it.id,
        name: it.name,
        size: it.size || null,
        price: it.price,
        quantity: it.quantity,
        imageUrl: it.imageUrl || null,
        comment: it.comment || null,
      })),
      total,
    };

    try {
      const payload = await Request(`/users/${user.id}/orders`, 'POST', body);
      if (payload && typeof payload === 'object') {
        // If the backend returns UserWithDetails, it has orders array
        if (Array.isArray(payload.orders)) {
          writeUserOrders(payload.orders);
          // Return the most recently created order
          return [...payload.orders].sort((a, b) => {
            const da = new Date(a.created_at || a.createdAt || 0).getTime();
            const db = new Date(b.created_at || b.createdAt || 0).getTime();
            return db - da;
          })[0];
        }

        // Fallback for when backend returns only the order object
        const userDb = readUserOrders();
        const orderToStore = {
          id: payload.id && payload.id !== user.id ? payload.id : (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()),
          orderNumber: payload.orderNumber || generateOrderNumber(),
          userId: payload.userId || user.id,
          items: Array.isArray(payload.items) ? payload.items : body.items,
          total: typeof payload.total === 'number' ? payload.total : total,
          status: payload.status || 'PLACED',
          created_at: payload.createdAt || payload.created_at || new Date().toISOString(),
        };
        
        // Avoid duplicate push if order already exists
        if (!userDb.find(o => o.id === orderToStore.id || o.orderNumber === orderToStore.orderNumber)) {
          userDb.push(orderToStore);
          writeUserOrders(userDb);
        }
        return orderToStore;
      }
    } catch (_err) {
      // Fallback to local
    }

    const order = {
      id: (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()),
      orderNumber: generateOrderNumber(),
      userId: user.id,
      items: body.items,
      total,
      status: 'PLACED',
      created_at: new Date().toISOString(),
    };
    const userDb = readUserOrders();
    userDb.push(order);
    writeUserOrders(userDb);
    return order;
  };

  const listOrders = (userId) => {
    const userOrders = readUserOrders();
    let source = userOrders;

    if (!Array.isArray(source) || source.length === 0) {
      source = readGlobalOrders();
    }

    const hasUserId = source.some((o) => o && typeof o === 'object' && 'userId' in o);
    const filtered = hasUserId && userId != null ? source.filter((o) => o.userId === userId) : source;

    const toEpochMs = (value) => {
      if (!value) return 0;
      if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
      if (typeof value === 'string') {
        const n = Number(value);
        if (!Number.isNaN(n)) return n < 1e12 ? n * 1000 : n;
        const d = new Date(value);
        const t = d.getTime();
        return Number.isNaN(t) ? 0 : t;
      }
      if (value instanceof Date) {
        const t = value.getTime();
        return Number.isNaN(t) ? 0 : t;
      }
      return 0;
    };

    return filtered
      .map((o) => ({
        id: o.id,
        orderNumber: o.orderNumber,
        shippingNumber: o.shippingNumber,
        total: typeof o.total === 'number' ? o.total : 0,
        items: Array.isArray(o.items) ? o.items : [],
        created_at: o.created_at || o.createdAt || null,
        status: o.status || 'PLACED',
      }))
      .sort((a, b) => toEpochMs(b.created_at) - toEpochMs(a.created_at));
  };

  const getOrder = (orderId) => {
    const userOrders = readUserOrders();
    const fromUser = userOrders.find((o) => o.id === orderId);
    if (fromUser) return fromUser || null;
    const global = readGlobalOrders();
    return global.find((o) => o.id === orderId) || null;
  };

  const value = useMemo(() => ({ placeOrder, listOrders, getOrder }), []);

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};

export const useOrders = () => {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used within OrdersProvider');
  return ctx;
};
